<template>
  <div>
    <BaseTitle :title="$t('content.attendance_list')" :yearPeriode="titleData" />
    <v-card class="pa-8 mb-6">
      <h3 class="app-section-title">{{ $t('content.class_name') }}</h3>
      <v-row>
        <v-col cols="12" md="4" class="py-0">
          <form-group :name="$t('content.session')" :validator="$v.form.sessionName">
            <template slot-scope="{ attrs }">
              <v-text-field
                v-model="form.sessionName"
                v-bind="attrs"
                :disabled="!editable"
                :label="$t('content.session')"
                @input="$v.form.sessionName.$touch()"
                @blur="$v.form.sessionName.$touch()"
              />
            </template>
          </form-group>
        </v-col>
        <v-col cols="12" md="4" class="py-0">
          <form-group :name="$t('form.date')" :validator="$v.form.attendanceDate" no-gutter>
            <template slot-scope="{ attrs }">
              <v-menu
                ref="pickerDate"
                v-model="pickerDate"
                :close-on-content-click="false"
                :return-value.sync="pickerDate"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template #activator="{ on, attrs:attrActivator }">
                  <v-text-field
                    v-model="form.attendanceDate"
                    :label="$t('form.date')"
                    :disabled="!editable"
                    readonly
                    v-bind="{ ...attrActivator, ...attrs }"
                    v-on="on"
                  >
                    <template #append>
                      <svg-icon icon="calendar" class="primary--text mr-3" />
                    </template>
                  </v-text-field>
                </template>
                <v-date-picker v-model="form.attendanceDate" :locale="$i18n.locale">
                  <v-spacer />
                  <v-btn text color="primary" @click="pickerDate = false">{{ $t('cancel') }}</v-btn>
                  <v-btn text color="primary" @click="$refs.pickerDate.save(form.attendanceDate)">{{ $t('ok') }}</v-btn>
                </v-date-picker>
              </v-menu>
            </template>
          </form-group>
        </v-col>
        <v-col cols="12" md="4" class="py-0">
          <form-group :name="$t('form.time')" :validator="$v.form.attendanceTime" no-gutter>
            <template slot-scope="{ attrs }">
              <v-menu
                ref="pickerTime"
                v-model="pickerTime"
                :close-on-content-click="false"
                :nudge-right="40"
                :return-value.sync="pickerTime"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs: attrActivator }">
                  <v-text-field
                    v-model="form.attendanceTime"
                    :label="$t('form.time')"
                    :disabled="!editable"
                    readonly
                    v-bind="{ ...attrActivator, ...attrs }"
                    v-on="on"
                  >
                    <template #append>
                      <svg-icon icon="clock" class="primary--text mr-3" />
                    </template>
                  </v-text-field>
                </template>

                <v-time-picker v-model="form.attendanceTime" :locale="$i18n.locale" @click:minute="$refs.pickerTime.save(form.attendanceTime)" />
              </v-menu>
            </template>
          </form-group>
        </v-col>
      </v-row>
    </v-card>

    <v-card class="px-6 py-2 mb-5">
      <div class="d-flex align-center justify-space-between">
        <h3 class="app-section-title">{{ $t('content.student_list') }}</h3>
        <div>
          <v-text-field v-model="search" :label="$t('search')">
            <template #prepend-inner>
              <svg-icon icon="ic-search" class="primary--text mr-3" />
            </template>
          </v-text-field>
        </div>
      </div>
      <v-divider />
      <div v-show="loadingStudents" class="text-center pa-5">
        <v-progress-circular indeterminate />
      </div>
      <div v-show="!loadingStudents" v-for="(student, index) in studentsFilter" :key="student.studentId">
        <div class="d-flex align-center justify-space-between">
          <div>
            <v-row v-if="student.birthdayToday === true">
              <v-col md="2" align="center" class="mt-3">
                <div class="iconBirthday">
                  <svg-icon icon="ic_cake" class="primary--text" />
                </div>
              </v-col>
              <v-col md="10">
                <div class="d-block">
                  <span
                    class="d-inline"
                    style="color: #8869AD;
  font-family: Montserrat;
  font-size: 10px;
  line-height: 12px;
  text-align: left;"
                  >
                    {{ $t('content.happy_birthday') }}
                  </span>
                </div>
                <div class="d-block">
                  <div>
                    <span>
                      <span> {{ student.fullName }} </span>
                    </span>
                  </div>
                </div>
              </v-col>
            </v-row>
            <div v-else>{{ student.fullName }}</div>
            <div v-if="student.newStudent" class="green--text">
              {{ $t('content.attendance_new_student') }}
            </div>
          </div>
          <div style="width: 200px">
            <form-group :name="$t('form.remarks')" :validator="$v.form.attendanceDetails.$each.$iter[index]" no-gutter>
              <template slot-scope="{ attrs }">
                <v-select
                  v-bind="attrs"
                  v-model="student.attendanceTypeId"
                  :disabled="!editable || student.newStudent"
                  :items="student.status ? itemTypes : itemTypesInactive"
                  :placeholder="$t('form.select_attendance')"
                >
                  <template #item="{item}">{{ $t(item.text) }}</template>
                  <template #selection="{item}">{{ $t(item.text) }}</template>
                </v-select>
              </template>
            </form-group>
          </div>
        </div>
        <v-divider v-if="index != studentsFilter.length - 1" />
      </div>
    </v-card>

    <v-card class="px-6 py-4 mb-5">
      <strong>{{ $t('form.remarks_content') }}</strong>
      <form-group :name="$t('form.remarks')" :validator="$v.form.remarks" no-gutter>
        <template slot-scope="{ attrs }">
          <v-textarea
            v-model="form.remarks"
            v-bind="attrs"
            :label="$t('form.remarks')"
            :disabled="!editable"
            @input="$v.form.remarks.$touch()"
            @blur="$v.form.remarks.$touch()"
          />
        </template>
      </form-group>
    </v-card>

    <div class="d-flex justify-end align-center">
      <v-btn color="primary" class="btn-primary-custom mr-3" outlined @click="back">{{ $t(edit ? 'back' : 'cancel') }}</v-btn>
      <v-btn v-if="editable" color="primary" :loading="loading" class="btn-primary-custom" @click="save">
        {{ $t(edit ? 'save' : 'submit') }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import moment from 'moment-timezone';
import { merge, cloneDeep } from 'lodash';
import { mapGetters } from 'vuex';
import { maxLength, required, requiredIf } from 'vuelidate/lib/validators';
import BaseTitle from '@/components/base/Title';

export default {
  name: 'AttendanceForm',
  components: {
    BaseTitle
  },
  props: {
    editable: {
      type: Boolean,
      default: true
    },
    type: {
      type: String
    }
  },
  computed: {
    ...mapGetters({
      itemTypes: 'Attendance/itemTypes',
      user: 'Auth/user',
      nameCourse: 'CourseList/nameCourse'
    }),
    studentsFilter() {
      if (!this.search) {
        return this.form.attendanceDetails;
      }
      return this.form.attendanceDetails.filter(student => student.fullName.toLowerCase().includes(this.search.toLowerCase()));
    },
    itemTypesInactive() {
      const copyItemTypes = cloneDeep(this.itemTypes);
      return copyItemTypes.map(item => {
        item.disabled = [1, 2].includes(item.value);
        return item;
      });
    }
  },
  validations() {
    return {
      form: {
        sessionName: { required, maxLength: maxLength(27) },
        attendanceDate: { required },
        attendanceTime: { required },
        remarks: { maxLength: maxLength(255) },
        attendanceDetails: {
          $each: {
            attendanceTypeId: {
              requiredIf: requiredIf(el => !el.newStudent)
            }
          }
        }
      }
    };
  },
  data: () => ({
    dataClass: '',
    edit: false,
    loading: false,
    loadingStudents: true,
    pickerDate: false,
    pickerTime: false,
    search: null,
    timezone: '',
    form: {
      sessionName: null,
      attendanceDate: '',
      attendanceTime: '',
      remarks: null,
      attendanceDetails: [],
      lastUpdate: ''
    },
    titleData: ''
  }),
  methods: {
    getTimeZone() {
      this.$store.dispatch('User/fetchProfile', this.currentId).then(res => {
        if (res.gmt === null) {
          res.gmt = 'Asia/Jakarta';
        }
        this.timezone = res.gmt;
        this.form.attendanceDate = moment()
          .tz(res.gmt)
          .format('YYYY-MM-DD');

        if (this.form.attendanceTime) return;
        this.form.attendanceTime = moment()
          .tz(res.gmt)
          .format('HH:mm');
      });
    },
    getDataClass() {
      if (!this.$route.params.classSectionId) {
        this.$store
          .dispatch('Class/fetchClassroomCourseName', { classId: this.$route.params.classId, courseId: this.$route.params.courseId })
          .then(res => {
            if (this.user.cerdasTypeId === 2 || this.user.cerdasTypeId === null) {
              this.$store.dispatch('SchoolClass/fetchDetail', this.$route.params.classId).then(resa => {
                this.dataClass = resa;
                this.titleData = res + ` ( ${resa.yearPeriode} ) `;
              });
            } else {
              this.$store.dispatch('Class/fetchListLiteDetail', this.$route.params.classId).then(resa => {
                this.dataClass = resa;
                this.titleData = res + ` ( ${resa.yearPeriode} ) `;
              });
            }
          });
      } else {
        this.$store.dispatch('ClassSection/getListDetail', { classSectionId: this.$route.params.classSectionId }).then(res => {
          if (this.user.cerdasTypeId === 2 || this.user.cerdasTypeId === null) {
            this.$store.dispatch('SchoolClass/fetchDetail', this.$route.params.classId).then(resa => {
              this.dataClass = resa;
              this.titleData = res.classroomName + ` ${res.numberClass}` + ` ( ${resa.yearPeriode} ) `;
            });
          }
        });
      }
    },
    getStudents() {
      const { classId, courseId } = this.$route.params;
      return new Promise(resolve => {
        this.$store
          .dispatch('Attendance/fetchStudents', {
            classId,
            courseId,
            classSectionId: this.$route.params.classSectionId ? this.$route.params.classSectionId : ''
          })
          .then(res => {
            this.form.attendanceDetails = res.map(student => {
              if (student.status == true) {
                student.attendanceTypeId = 1;
              } else {
                student.attendanceTypeId = 3;
              }
              return student;
            });
            resolve(res);
          })
          .finally(() => (this.loadingStudents = false));
      });
    },
    getData() {
      const { id } = this.$route.params;
      return new Promise(resolve => {
        this.$store.dispatch('Attendance/fetchDetail', id).then(res => {
          Object.keys(this.form)
            .filter(field => field !== 'attendanceDetails')
            .forEach(field => (this.form[field] = res[field]));
          resolve(res.attendanceDetails);
        });
      });
    },
    save() {
      this.$v.$touch();
      if (this.$v.$invalid) return;
      this.loading = true;
      const { classId, courseId, id } = this.$route.params;

      this.$store
        .dispatch(`Attendance/${this.edit ? 'update' : 'save'}`, {
          id,
          classId,
          courseId,
          timezone: this.timezone,
          ...this.form,
          classSectionId: this.$route.params.classSectionId ? this.$route.params.classSectionId : ''
        })
        .then(() =>
          this.$router.push({
            name:
              this.edit && !this.$route.params.classSectionId
                ? 'TeacherAttendanceUpdateSuccess'
                : !this.edit && !this.$route.params.classSectionId
                ? 'TeacherAttendanceSuccess'
                : this.edit && this.$route.params.classSectionId
                ? 'TeacherAttendanceSectionUpdateSuccess'
                : 'TeacherAttendanceSectionSuccess'
          })
        )
        .finally(() => (this.loading = false));
    },
    back() {
      if (this.type === 'teacher') {
        if (this.$route.params.classSectionId) {
          this.$router.push({ name: 'TeacherAttendanceSectionIndex' });
        } else {
          this.$router.push({ name: 'TeacherAttendanceIndex' });
        }
      } else {
        this.$router.push({ name: 'AdvisorAttendanceIndex' });
      }
    }
  },
  created() {
    this.getTimeZone();
    this.getDataClass();
    this.$store.commit('table/RESET_PAGEABLE');
    if (this.$route.params.id) {
      this.edit = true;
      this.loadingStudents = true;
      Promise.all([this.getData()])
        .then(res => this.$set(this.form, 'attendanceDetails', merge(...res)))
        .finally(() => (this.loadingStudents = false));
    } else {
      this.getStudents();
    }
  }
};
</script>
